import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";

/**
 * Outer gate for `/admin/*`.
 *
 * Admits anyone holding at least one staff permission, rather than checking for
 * a hard-coded role list - so `support`, `compliance`, `operations`, `finance`
 * and `admin` all reach the portal and then see only the sections their
 * permissions allow.
 *
 * Permissions resolve from the authenticated user's role in every environment.
 * This component intentionally does no environment sniffing of its own: it
 * reflects whatever the backend says the caller can do, so the UI can never be
 * more permissive than the API.
 */
export function AdminRoute() {
  const { loading, isAuthenticated } = useAuth();
  const { isStaff, isLoadingPermissions, permissionsError } = usePermissions();
  const location = useLocation();

  if (loading || isLoadingPermissions) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-sm text-slate-500">
        Checking admin access…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // A failed permissions lookup is treated as "no access": fail closed.
  if (permissionsError || !isStaff) {
    return <Navigate to="/home" replace />;
  }

  return <Outlet />;
}
